import { ref, update } from 'firebase/database';

// Firebase RTB has a 512 node limit per multi-path update
// Using 200 as safe limit (accounts for 2 paths per device = 100 devices per batch)
export const FIREBASE_BATCH_LIMIT = 200;

/**
 * Performs batched Firebase updates with retry logic and progress tracking
 *
 * @param {Object} db - Firebase database instance
 * @param {Object} updates - Object with path:value pairs to update
 * @param {Object} options - Configuration options
 * @param {Function} options.onProgress - Callback with progress percentage (0-100)
 * @param {Function} options.onBatchComplete - Callback when each batch completes
 * @param {number} options.retryCount - Number of retries per batch (default: 3)
 * @param {number} options.retryDelay - Base delay between retries in ms (default: 1000)
 * @param {number} options.batchDelay - Delay between batches in ms (default: 100)
 * @returns {Promise<Object>} Results with success and failed paths
 */
export async function batchUpdate(db, updates, options = {}) {
    const {
        onProgress,
        onBatchComplete,
        retryCount = 3,
        retryDelay = 1000,
        batchDelay = 100
    } = options;

    const paths = Object.keys(updates);
    const totalPaths = paths.length;

    if (totalPaths === 0) {
        return { success: [], failed: [], totalBatches: 0 };
    }

    const totalBatches = Math.ceil(totalPaths / FIREBASE_BATCH_LIMIT);
    const results = {
        success: [],
        failed: [],
        totalBatches,
        errors: []
    };

    for (let i = 0; i < totalPaths; i += FIREBASE_BATCH_LIMIT) {
        const batchPaths = paths.slice(i, i + FIREBASE_BATCH_LIMIT);
        const batchUpdates = {};
        batchPaths.forEach(path => {
            batchUpdates[path] = updates[path];
        });

        const batchNumber = Math.floor(i / FIREBASE_BATCH_LIMIT) + 1;
        let lastError = null;
        let success = false;

        // Retry logic with exponential backoff
        for (let attempt = 1; attempt <= retryCount; attempt++) {
            try {
                await update(ref(db), batchUpdates);
                results.success.push(...batchPaths);
                success = true;
                break;
            } catch (error) {
                lastError = error;
                console.error(`Batch ${batchNumber}/${totalBatches} attempt ${attempt} failed:`, error.message);

                if (attempt < retryCount) {
                    // Exponential backoff: 1s, 2s, 4s...
                    const delay = retryDelay * Math.pow(2, attempt - 1);
                    await new Promise(r => setTimeout(r, delay));
                }
            }
        }

        if (!success) {
            results.failed.push(...batchPaths);
            results.errors.push({
                batch: batchNumber,
                paths: batchPaths,
                error: lastError?.message || 'Unknown error'
            });
        }

        // Callback for batch completion
        onBatchComplete?.({
            batch: batchNumber,
            totalBatches,
            success,
            pathsInBatch: batchPaths.length,
            error: lastError
        });

        // Progress callback
        const progress = Math.min(((i + FIREBASE_BATCH_LIMIT) / totalPaths) * 100, 100);
        onProgress?.(progress);

        // Small delay between batches to prevent overwhelming Firebase
        if (i + FIREBASE_BATCH_LIMIT < totalPaths && batchDelay > 0) {
            await new Promise(r => setTimeout(r, batchDelay));
        }
    }

    return results;
}

/**
 * Helper to count devices from path updates
 * Assumes paths like mdm/devices/{deviceId}/field
 *
 * @param {string[]} paths - Array of Firebase paths
 * @returns {number} Unique device count
 */
export function countDevicesFromPaths(paths) {
    const deviceIds = new Set();
    paths.forEach(path => {
        const match = path.match(/mdm\/devices\/([^/]+)/);
        if (match) {
            deviceIds.add(match[1]);
        }
    });
    return deviceIds.size;
}

/**
 * Builds command updates for multiple devices
 *
 * @param {string[]} deviceIds - Array of device IDs
 * @param {string} command - Command to send
 * @param {boolean} setFeedback - Whether to set feedback to 'pending'
 * @returns {Object} Updates object for batchUpdate
 */
export function buildCommandUpdates(deviceIds, command, setFeedback = true) {
    const updates = {};
    for (const deviceId of deviceIds) {
        updates[`mdm/devices/${deviceId}/command`] = command;
        if (setFeedback) {
            updates[`mdm/devices/${deviceId}/feedback`] = 'pending';
        }
    }
    return updates;
}

/**
 * Builds volume updates for multiple devices
 *
 * @param {string[]} deviceIds - Array of device IDs
 * @param {number} volumeLevel - Volume level (0-100)
 * @returns {Object} Updates object for batchUpdate
 */
export function buildVolumeUpdates(deviceIds, volumeLevel) {
    const updates = {};
    for (const deviceId of deviceIds) {
        updates[`mdm/devices/${deviceId}/volume_level`] = volumeLevel;
    }
    return updates;
}
